/**
 * Phase C.1 — Technical Hard Envelope.
 *
 * Extends the existing `analyzeSafety()` (src/safety.ts) rather than
 * duplicating it: `assessTechnicalConstraints` runs `analyzeSafety` first and
 * merges its violations, then adds exactly one net-new hard rule this repo
 * does not yet have anywhere — the independently-resolved projectile
 * emission-rate cap (docs/gameplay/skill-value-engine-contract.md §13,
 * phase-c1-constraint-model-audit.md §D/§H). Every other "hard envelope"
 * example the contract lists (finite numerics, bounded duration, trigger
 * depth/fan-out, resource-loop/cycle rejection, ...) is already enforced by
 * `analyzeSafety`/`analyzeLoadoutCycles` and is deliberately not
 * reimplemented here (audit §A/§B).
 *
 * A structural violation here is unconditional: no economic drawback credit
 * can ever flip `ok` back to `true` (phase brief §5/§37/§85; enforced simply
 * by the economic layer never being allowed to see, let alone rewrite,
 * this result — see src/constraints/economic.ts).
 */

import type { Skill } from '../types.js';
import type { BalanceProfile } from '../profile.js';
import { SVP_0_1 } from '../profile.js';
import { analyzeSafety, type Violation } from '../safety.js';
import { isFiniteNumber } from '../math.js';
import { castsByCooldown } from '../eval/cadence.js';
import { countProjectileEmissionsPerActivation } from './projectile.js';

/**
 * Versioned, product-facing hard limits that are genuinely new in C.1 (not
 * already present in `BalanceProfile.limits`/`StructuralLimits`). Existing
 * structural limits (depth, fan-out, tick, duration, ...) stay owned by
 * `BalanceProfile` and are not duplicated into this shape (phase brief §10).
 */
export interface TechnicalConstraintProfile {
  version: string;
  projectile: {
    /** Bootstrap hard runtime cap (contract §13): 20/s. Confirmed, not a
     * calibration placeholder — distinct from the economic curve
     * coefficients in `ConstraintProfile.economic`, which remain TEST_ONLY. */
    maxIndependentEmissionsPerSecondPerSkill: number;
  };
  /**
   * Orbit contact precision. **Ratified 2026-09-20 from measurement**, not a
   * calibration placeholder — the evidence is
   * `docs/archive/gameplay/experiments/2026-09-19-orbit-precision-study.md`.
   *
   * What the evidence does and does not say:
   *
   * - Across 216 geometric conditions the implementation disagreed with an
   *   independent oracle **nowhere** at 0.02 m, both at the study's 0.5 m
   *   target radius (§3) and at the smallest hitbox this repository declares,
   *   0.35 m (§3.1, `src/encounter/fixtures-v0.ts:11`). Loosening to 0.05 m
   *   begins missing contacts.
   * - **The oracle's own worst-case blind spot is 0.0118 m.** Tolerances
   *   below that cannot be adjudicated by this evidence at all, so 0.02 m is
   *   the tightest value the study positively distinguishes. "No
   *   disagreement observed at 0.02 m" is the claim; "verified correct below
   *   0.0118 m" is not.
   */
  orbit: {
    /** How far a straight sub-step may stray from the true arc, in metres. */
    contactToleranceM: number;
    /**
     * Most sub-steps ONE orbiting instance may use in ONE tick.
     *
     * **Per tick at 20 Hz**, the rate the study measured at (`dt = 0.05`)
     * and the production default (`DEFAULT_AUTHORITATIVE_WORLD_CONFIG`).
     * Stated rather than parameterised, the same way burst legality states
     * `OFFENSE_DRIVER_MAX_DURATION_TICKS` as "400s at 20Hz" and keeps one
     * conversion as the single source of truth
     * (`docs/gameplay/phase-b1-burst-legality.md`). A slower tick sweeps
     * more arc per tick and so needs more sub-steps; that is a property of
     * the world it runs in, not a second number to configure here.
     *
     * **This is a per-orb budget, not a system-wide performance guarantee.**
     * It bounds the work a single orb can demand of a single tick; it says
     * nothing about the total cost of many orbs, and multiplying it by an orb
     * count is not a proof that the result runs. Whole-system performance
     * stays with Gate P8's many-orb verification, which this number does not
     * discharge — exactly as `maxSpawnedAttacks` bounds attack COUNT without
     * vouching for attack WORKLOAD (see `composite-technical.ts`).
     *
     * 128 admits every condition measured at 0.02 m (worst 106, three turns
     * per tick) and refuses roughly beyond 3.5 revolutions per tick at
     * r = 5 m — about 70 revolutions per second, far outside any authored
     * range: every plausible orbit measured needs at most 13 sub-steps, and
     * even 20 revolutions per second needs 36.
     *
     * A combination needing more is REFUSED for the whole tick, never
     * approximated and never partially applied.
     */
    maxContactStepsPerInstancePerTick: number;
  };
}

/**
 * The tick rate `orbit.maxContactStepsPerInstancePerTick` is stated at —
 * the "per tick at 20 Hz" above, as the one conversion every reader shares
 * rather than a `20` repeated at each call site (the same arrangement as
 * `OFFENSE_DRIVER_TICK_RATE_HZ`). Not a world setting: a world runs at its
 * own `tickRate` and is checked against that; this is only the reference a
 * plan is judged at when no world is in hand (the composite compiler).
 */
export const ORBIT_PRECISION_REFERENCE_TICK_RATE_HZ = 20;

export const C1_BOOTSTRAP_TECHNICAL_PROFILE: TechnicalConstraintProfile = {
  version: 'c1-bootstrap-1',
  projectile: { maxIndependentEmissionsPerSecondPerSkill: 20 },
  orbit: { contactToleranceM: 0.02, maxContactStepsPerInstancePerTick: 128 },
};

export interface ProjectileEmissionAnalysis {
  /** Independently-resolved projectile deliveries fired per single
   * activation, counted structurally (root delivery + every reachable
   * `secondary_attack` with `delivery.type: 'projectile'`), the same
   * "worst-case structural presence, not runtime probability" convention
   * `analyzeSafety`'s own `spawnedAttacks` counter already uses. */
  emissionsPerActivation: number;
  /** Worst-case activations resolvable within any 1-second window,
   * reusing `castsByCooldown(skill, 1)` unmodified (eval/cadence.ts) — the
   * same function production already uses for burst-window cast counting.
   * `Infinity` for a non-finite/zero effective cycle time. */
  activationsPerSecond: number;
  /** `emissionsPerActivation * activationsPerSecond`. */
  independentEmissionsPerSecond: number;
  /** `false` only when the skill has ≥1 projectile emission AND its own
   * activation timing is non-finite/invalid, so no rate can be computed at
   * all (phase brief §85: never assume an unresolvable value is under the
   * cap). `analyzeSafety` will independently flag the same invalid timing. */
  computable: boolean;
}

function timingIsFinite(skill: Skill): boolean {
  // ACTIVE.1: source-relative timing has no static cooldown to analyze here
  // — this is a generation/constraints-time diagnostic with no SourceSnapshot
  // available (audit §L/§Q7); route to the existing `computable: false`
  // sentinel below rather than let `castsByCooldown` throw uncaught.
  if (skill.sourceRelativeTiming !== undefined) return false;
  const fields: (number | undefined)[] = [
    skill.cooldownS,
    skill.castTimeS,
    skill.recoveryS,
    skill.chargeRechargeS,
  ];
  return fields.every((v) => v === undefined || isFiniteNumber(v));
}

/** Deterministic static analyzer for contract §13/§16-19. */
export function analyzeProjectileEmissionRate(skill: Skill): ProjectileEmissionAnalysis {
  const emissionsPerActivation = countProjectileEmissionsPerActivation(skill);
  if (emissionsPerActivation === 0) {
    return { emissionsPerActivation: 0, activationsPerSecond: 0, independentEmissionsPerSecond: 0, computable: true };
  }
  if (!timingIsFinite(skill)) {
    return { emissionsPerActivation, activationsPerSecond: NaN, independentEmissionsPerSecond: NaN, computable: false };
  }
  const activationsPerSecond = castsByCooldown(skill, 1);
  const independentEmissionsPerSecond = Number.isFinite(activationsPerSecond)
    ? emissionsPerActivation * activationsPerSecond
    : Infinity;
  return { emissionsPerActivation, activationsPerSecond, independentEmissionsPerSecond, computable: true };
}

export interface TechnicalDiagnostics {
  projectileEmissionRate: ProjectileEmissionAnalysis;
}

export interface TechnicalConstraintResult {
  /** true iff no error-severity violation exists, from analyzeSafety() or
   * from this layer's own net-new checks. */
  ok: boolean;
  violations: Violation[];
  diagnostics: TechnicalDiagnostics;
}

/**
 * Unified technical assessment: existing `analyzeSafety()` + the C.1
 * projectile emission-rate cap. `balanceProfile` is only needed to drive the
 * reused `analyzeSafety` call (its own `StructuralLimits`); it is not part
 * of `TechnicalConstraintProfile` and defaults to `SVP_0_1` to match
 * `appraiseSkill`'s existing default (src/index.ts).
 */
export function assessTechnicalConstraints(
  skill: Skill,
  technicalProfile: TechnicalConstraintProfile,
  balanceProfile: BalanceProfile = SVP_0_1,
): TechnicalConstraintResult {
  const safety = analyzeSafety(skill, balanceProfile);
  const violations: Violation[] = [...safety.violations];

  const projectileEmissionRate = analyzeProjectileEmissionRate(skill);
  if (!projectileEmissionRate.computable) {
    violations.push({
      code: 'UNRESOLVED_DYNAMIC_EMISSION_RATE',
      severity: 'error',
      message:
        'skill has projectile emissions but its activation timing is non-finite/invalid; ' +
        'independent emission rate cannot be statically resolved and is not assumed safe',
    });
  } else if (
    projectileEmissionRate.independentEmissionsPerSecond >
    technicalProfile.projectile.maxIndependentEmissionsPerSecondPerSkill + 1e-9
  ) {
    violations.push({
      code: 'PROJECTILE_EMISSION_RATE_EXCEEDED',
      severity: 'error',
      message:
        `independent projectile emissions/s (${projectileEmissionRate.independentEmissionsPerSecond}) exceeds ` +
        `the hard cap (${technicalProfile.projectile.maxIndependentEmissionsPerSecondPerSkill})`,
    });
  }

  return {
    ok: !violations.some((v) => v.severity === 'error'),
    violations,
    diagnostics: { projectileEmissionRate },
  };
}
